import { useState, useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  initializeRecentlyViewed,
  removeFromRecentlyViewed as removeFromRedux,
  clearRecentlyViewed as clearRedux,
} from '@/store/slices/uiSlice'
import apiService from '@/services/api'
import { RecentlyViewedRecord } from '@/types'

interface UseRecentlyViewedOptions {
  limit?: number
  loadOnMount?: boolean
}

export function useRecentlyViewed({ limit = 10, loadOnMount = false }: UseRecentlyViewedOptions = {}) {
  const dispatch = useAppDispatch()
  const reduxRecords = useAppSelector((state) => state.ui.recentlyViewedRecords)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const records = await apiService.getRecentlyViewedFiles(limit)
      dispatch(initializeRecentlyViewed(records))
    } catch {
      setError('Failed to load recently viewed files')
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, limit])

  useEffect(() => {
    if (loadOnMount) {
      refresh()
    }
  }, [loadOnMount, refresh])

  const remove = useCallback(
    async (fileId: number) => {
      dispatch(removeFromRedux(fileId))
      try {
        await apiService.removeFileFromRecentlyViewed(fileId)
      } catch {
        refresh()
      }
    },
    [dispatch, refresh]
  )

  const clear = useCallback(async () => {
    dispatch(clearRedux())
    try {
      await apiService.clearRecentlyViewed()
    } catch {
      refresh()
    }
  }, [dispatch, refresh])

  return {
    items: reduxRecords as RecentlyViewedRecord[],
    isLoading,
    error,
    refresh,
    remove,
    clear,
  }
}
